import React, { useState } from "react";
import { GlobalBiznessLogo } from "./GlobalBiznessLogo";
import { COMPANY_CONFIG } from "../data/companyConfig";
import { Phone, Mail, Menu, X, LogIn, ArrowRight } from "lucide-react";

export const Navbar = ({ onSignIn, signInLabel = "Login", onContactClick }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const scrollTo = (id) => {
        setMobileMenuOpen(false);
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-xs">
            {/* Top Helpline & Office Strip (Blue & Green Accents) */}
            <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-emerald-900 text-white text-xs py-1.5 px-4 sm:px-8 border-b border-blue-950">
                <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2">
                    <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1.5 font-medium text-emerald-300">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse inline-block" />
                            BBPS & Digital Utility Network Live
                        </span>
                        <span className="hidden md:inline text-blue-300/40">|</span>
                        <span className="hidden md:inline text-blue-100">
                            Hyderabad Head Office: Madhapur, Hitex Bizness Square
                        </span>
                    </div>

                    <div className="flex items-center gap-5 ml-auto text-xs">
                        <a
                            href={`tel:${COMPANY_CONFIG.phoneClean}`}
                            className="flex items-center gap-1.5 text-emerald-300 hover:text-white font-semibold transition-colors"
                            id="top-bar-helpline-link"
                        >
                            <Phone className="w-3.5 h-3.5 text-emerald-300" />
                            <span>Helpline: {COMPANY_CONFIG.phone}</span>
                        </a>
                        <span className="text-blue-300/40 hidden sm:inline">|</span>
                        <a
                            href={`mailto:${COMPANY_CONFIG.email}`}
                            className="hidden sm:inline text-blue-100 hover:text-white transition-colors"
                            id="top-bar-email-link"
                        >
                            <Mail className="w-3.5 h-3.5 text-emerald-300 inline mr-1" />
                            <span>{COMPANY_CONFIG.email}</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Main Header Navigation (White Background) */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-20">
                    {/* Logo */}
                    <button
                        onClick={() => scrollTo("hero-section")}
                        className="flex items-center text-left focus:outline-hidden cursor-pointer"
                        id="nav-brand-logo-btn"
                    >
                        <GlobalBiznessLogo size="md" variant="light" />
                    </button>

                    {/* Desktop Nav Links */}
                    <nav className="hidden lg:flex items-center gap-7 text-sm font-semibold text-slate-700">
                        <button
                            onClick={() => scrollTo("hero-section")}
                            className="hover:text-blue-600 transition-colors py-2 cursor-pointer"
                            id="nav-home-btn"
                        >
                            Home
                        </button>
                        <button
                            onClick={() => scrollTo("about-section")}
                            className="hover:text-blue-600 transition-colors py-2 cursor-pointer"
                            id="nav-about-btn"
                        >
                            About Us
                        </button>
                        <button
                            onClick={() => scrollTo("services-section")}
                            className="hover:text-blue-600 transition-colors py-2 cursor-pointer"
                            id="nav-services-btn"
                        >
                            Services
                        </button>
                        <button
                            onClick={() => scrollTo("payment-solutions-section")}
                            className="hover:text-blue-600 transition-colors py-2 cursor-pointer"
                            id="nav-payment-solutions-btn"
                        >
                            Payment Solutions
                        </button>
                        <button
                            onClick={() => scrollTo("why-choose-us")}
                            className="hover:text-blue-600 transition-colors py-2 cursor-pointer"
                            id="nav-why-choose-us-btn"
                        >
                            Why Choose Us
                        </button>
                        <button
                            onClick={() => scrollTo("contact-section")}
                            className="hover:text-blue-600 transition-colors py-2 cursor-pointer"
                            id="nav-contact-btn"
                        >
                            Contact Us
                        </button>
                    </nav>

                    {/* Right Actions: Login & Join Today (Blue & Green Buttons) */}
                    <div className="hidden sm:flex items-center gap-3">
                        <button
                            onClick={onSignIn}
                            className="px-4 py-2.5 rounded-xl font-bold text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors flex items-center gap-1.5 cursor-pointer"
                            id="nav-login-btn"
                        >
                            <LogIn className="w-3.5 h-3.5 text-blue-600" />
                            <span>{signInLabel}</span>
                        </button>

                        <button
                            onClick={onContactClick}
                            className="px-5 py-2.5 rounded-xl font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-500 shadow-md shadow-emerald-600/20 transition-all flex items-center gap-1.5 cursor-pointer"
                            id="nav-join-today-btn"
                        >
                            <span>Join Today</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    {/* Mobile Hamburger Toggle */}
                    <div className="flex lg:hidden items-center gap-2">
                        <button
                            onClick={onSignIn}
                            className="px-3 py-1.5 rounded-lg font-bold text-xs text-white bg-blue-600 sm:hidden"
                        >
                            {signInLabel}
                        </button>
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="p-2 rounded-lg text-slate-700 hover:text-blue-600 hover:bg-slate-100 transition-colors"
                            aria-label="Toggle menu"
                        >
                            {mobileMenuOpen ? (
                                <X className="w-6 h-6" />
                            ) : (
                                <Menu className="w-6 h-6" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
                <div className="lg:hidden bg-white border-b border-slate-200 px-4 pt-2 pb-6 space-y-2 shadow-xl animate-in slide-in-from-top-2 duration-150 text-slate-800">
                    <button
                        onClick={() => scrollTo("hero-section")}
                        className="block w-full text-left py-2 px-3 rounded-lg text-sm font-semibold hover:bg-blue-50 hover:text-blue-600"
                    >
                        Home
                    </button>
                    <button
                        onClick={() => scrollTo("about-section")}
                        className="block w-full text-left py-2 px-3 rounded-lg text-sm font-semibold hover:bg-blue-50 hover:text-blue-600"
                    >
                        About Us
                    </button>
                    <button
                        onClick={() => scrollTo("services-section")}
                        className="block w-full text-left py-2 px-3 rounded-lg text-sm font-semibold hover:bg-blue-50 hover:text-blue-600"
                    >
                        Services
                    </button>
                    <button
                        onClick={() => scrollTo("payment-solutions-section")}
                        className="block w-full text-left py-2 px-3 rounded-lg text-sm font-semibold hover:bg-blue-50 hover:text-blue-600"
                    >
                        Payment Solutions
                    </button>
                    <button
                        onClick={() => scrollTo("why-choose-us")}
                        className="block w-full text-left py-2 px-3 rounded-lg text-sm font-semibold hover:bg-blue-50 hover:text-blue-600"
                    >
                        Why Choose Us
                    </button>
                    <button
                        onClick={() => scrollTo("contact-section")}
                        className="block w-full text-left py-2 px-3 rounded-lg text-sm font-semibold hover:bg-blue-50 hover:text-blue-600"
                    >
                        Contact Us
                    </button>

                    <div className="pt-3 border-t border-slate-200 flex flex-col gap-2">
                        <button
                            onClick={() => {
                                setMobileMenuOpen(false);
                                onContactClick();
                            }}
                            className="py-2.5 px-3 rounded-xl text-xs font-bold text-white bg-emerald-600 flex items-center justify-center gap-2"
                        >
                            <span>Join Today as Partner</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                        <a
                            href={`tel:${COMPANY_CONFIG.phoneClean}`}
                            className="py-2 px-3 rounded-xl text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 flex items-center justify-center gap-2"
                        >
                            <Phone className="w-3.5 h-3.5" />
                            <span>Call Helpline: {COMPANY_CONFIG.phone}</span>
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};
